// src/pages/AdminNoticeRegister.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput, formatDate } from "@/utils";
import { Confirm, InputPopup, Pagination, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";

// rentCorpApi (사업자 조회)
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";

// parkingLotApi
import { useGetParkingLotByIdQuery, usePostParkingLotMutation, usePutParkingLotMutation } from "@/redux/parkingLotApi";

/**
 * AdminNoticeRegister
 * - 릴리즈 노트 등록 (CREATE) or 수정 (UPDATE)
 * - URL 파라미터: parkingId
 * - CREATE: postParkingLot
 * - UPDATE: getParkingLotById로 상세조회 → putParkingLot
 */
export const AdminReleaseRegister = () => {
    const { parkingId } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 릴리즈 노트 필드
        name: "", // 주차장명
        rentCorpId: 0, // 사업자 지점
        totalArea: "", // 인가 면적
        totalCount: "", // 등록대수
        remainCount: "", // 잔여대수
        releaseCount: "", // 출고대수
        lessor: "", // 임대인
        lessorPhone: "", // 임대인 연락처
        officeUseYn: "Y", // 사무실 이용 여부
        bCode: "",
        address: "",
        addressDetail: "",
        parkingLotStatus: null,
        memo: "",
        firstDate: "",
    });

    const { mode, toastMsg, confirmStatus, addressPopupStatus } = formData;

    // 1) 모드 설정: parkingId 유무
    useEffect(() => {
        if (parkingId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: parkingId,
            }));
        }
    }, [parkingId, setFormData]);

    // 2) UPDATE 모드일 경우, 릴리즈 노트 상세조회
    const {
        data: parkingLotDetail,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetParkingLotByIdQuery(parkingId, {
        skip: !parkingId, // parkingId 없으면 호출X
    });

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (parkingLotDetail && parkingLotDetail.data) {
            const d = parkingLotDetail.data;
            setFormData((prev) => ({
                ...prev,
                name: d.name || "",
                rentCorpId: d.rentCorpId || 0,
                totalArea: d.totalArea || "",
                totalCount: d.totalCount || "",
                remainCount: d.remainCount || "",
                releaseCount: d.releaseCount || "",
                lessor: d.lessor || "",
                lessorPhone: d.lessorPhone || "",
                officeUseYn: d.officeUseYn || undefined,
                bCode: d.bCode || "",
                address: d.address || "",
                addressDetail: d.addressDetail || "",
            }));
        }
    }, [parkingLotDetail, setFormData]);

    // 4) 내 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData?.data?.map((item) => ({
            value: item.id,
            label: item.corpName,
        })) || [];

    // 5) post / put mutation
    const [postParkingLot, { isLoading: isPostLoading }] = usePostParkingLotMutation();
    const [putParkingLot, { isLoading: isPutLoading }] = usePutParkingLotMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                rentCorpId: formData.rentCorpId,
                totalArea: formData.totalArea,
                totalCount: formData.totalCount,
                remainCount: formData.remainCount,
                releaseCount: formData.releaseCount,
                lessor: formData.lessor,
                lessorPhone: formData.lessorPhone,
                officeUseYn: formData.officeUseYn,
                bCode: formData.bCode,
                address: formData.address,
                addressDetail: formData.addressDetail,
            };

            if (mode === "UPDATE") {
                // 수정
                await putParkingLot({
                    parkingLotId: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await postParkingLot(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/erp/notice"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `릴리즈 노트 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 8) 삭제 Confirm (미구현)
    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
            toastMsg: "삭제 기능은 미구현 상태입니다.",
        }));
    };

    // 9) 삭제 버튼
    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 릴리즈 노트 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 10) 릴리즈 노트 내용 삭제 Confirm
    const handleReleaseNoteDeleteConfirm = async (itemId) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            // TODO: 릴리즈 노트 내용 삭제 API 호출
            console.log("삭제할 항목 ID:", itemId);

            // API 호출 예시
            // await deleteReleaseNoteContent(itemId).unwrap();

            setFormData((prev) => ({
                ...prev,
                toastMsg: "릴리즈 노트 내용이 삭제되었습니다.",
            }));
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "삭제 중 오류가 발생했습니다.",
            }));
        }
    };

    // 11) 릴리즈 노트 내용 삭제 버튼
    const handleReleaseNoteDelete = (itemId) => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 릴리즈 노트 내용을 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: () => handleReleaseNoteDeleteConfirm(itemId) },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    const handleAddContent = (item) => {
        const checkboxes = [
            {
                name: "platform",
                label: "플랫폼 구분",
                options: [
                    { value: "APP", label: "APP" },
                    { value: "WEB", label: "WEB" },
                ],
                value: [], // 초기값: 빈 배열
            },
        ];

        const textareas = [
            {
                name: "content",
                label: "내용",
                placeholder: "릴리즈 노트 내용을 입력해주세요.",
                rows: 6,
                value: "",
            },
        ];

        const quillEditors = [
            {
                name: "content",
                label: "내용",
                placeholder: "릴리즈 노트 내용을 입력해주세요.",
                value: "",
            },
        ];

        const buttons = [
            {
                label: "취소",
                className: "btn-cancel",
                onClick: () => setFormData((prev) => ({ ...prev, releaseNotePopupStatus: false })),
            },
            {
                label: "확인",
                className: "btn-primary",
                closeOnClick: false,
                onClick: async (values) => {
                    console.log("체크박스 값:", values.checkboxValues);
                    console.log("텍스트에어리어 값:", values.textareaValues);
                    console.log("Quill 에디터 값:", values.quillValues);

                    // 선택된 플랫폼과 내용 추출
                    const selectedPlatforms = values.checkboxValues.platform || [];
                    const content = values.textareaValues.content || values.quillValues.content || "";

                    // 유효성 검사
                    if (selectedPlatforms.length === 0) {
                        setFormData((prev) => ({
                            ...prev,
                            toastMsg: "플랫폼을 선택해주세요.",
                        }));
                        return;
                    }

                    if (!content.trim()) {
                        setFormData((prev) => ({
                            ...prev,
                            toastMsg: "내용을 입력해주세요.",
                        }));
                        return;
                    }

                    // TODO: 릴리즈 노트 내용 추가 API 호출
                    const body = {
                        platforms: selectedPlatforms,
                        content: content,
                    };

                    console.log("저장할 데이터:", body);

                    // API 호출 예시
                    // try {
                    //     const response = await addReleaseNoteContent(body).unwrap();
                    //     console.log(response);
                    //     refetch();
                    // } catch (error) {
                    //     console.error(error);
                    // }

                    setFormData((prev) => ({
                        ...prev,
                        releaseNotePopupStatus: false,
                        toastMsg: "릴리즈 노트 내용이 추가되었습니다.",
                    }));
                },
            },
        ];

        setFormData((prev) => ({
            ...prev,
            releaseNotePopupStatus: true,
            popupContent: (
                <InputPopup
                    title="릴리즈 노트 내용 추가"
                    description="플랫폼을 선택하고 릴리즈 노트 내용을 입력해주세요."
                    checkboxes={checkboxes}
                    // textareas={textareas} // textarea 사용 시
                    quillEditors={quillEditors} // ReactQuill 사용 시 (textareas 대신)
                    buttons={buttons}
                    onClose={() => setFormData((prev) => ({ ...prev, releaseNotePopupStatus: false }))}
                />
            ),
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">{mode === "CREATE" ? "릴리즈 노트 등록" : "릴리즈 노트 수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    릴리즈 노트 삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                {mode === "CREATE" ? "등록" : "수정"}
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">릴리즈 노트 내용</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ReleaseNoteInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">릴리즈 노트 내용</span>
                                    <div className="form-item_etc">
                                        <button type="button" className="btn btn-fourth" onClick={handleAddContent}>
                                            내용 추가
                                        </button>
                                    </div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <ReleaseNoteTable
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                            onDeleteItem={handleReleaseNoteDelete}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 릴리즈 노트 내용 추가/수정 팝업 */}
            {formData.releaseNotePopupStatus && formData.popupContent}

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

// --------------------------------------------------------------------
// ReleaseNoteInfo Component
// --------------------------------------------------------------------
const ReleaseNoteInfo = ({ formData, handleChange, setFormData, corpOptions }) => {
    const {
        name,
        rentCorpId,
        totalArea,
        totalCount,
        remainCount,
        releaseCount,
        lessor,
        lessorPhone,
        officeUseYn,
        address,
        addressDetail,
        bCode,
        parkingLotStatus,
        memo,
        firstDate,
    } = formData;
    const [value, setValue] = useState("");

    // 사무실 이용 여부 select options
    const officeUseOptions = [
        { value: "Y", label: "사용" },
        { value: "N", label: "미사용" },
    ];

    return (
        <>
            {/* 제목 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>제목</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="ex) 5월 4주차 릴리즈 노트" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 버전 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>버전</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="ex) 1.0.0" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 시작일자 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>작업일자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="firstDate"
                            placeholder="ex) 2025-05-01"
                            value={formatDate(firstDate)}
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                    <span className="input-esset">~</span>
                    <div className="form-element">
                        <input
                            type="text"
                            name="firstDate"
                            placeholder="ex) 2025-05-01"
                            value={formatDate(firstDate)}
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            {/* 배포일자 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>배포일자</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="firstDate"
                            placeholder="ex) 2025-05-01"
                            value={formatDate(firstDate)}
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            {/* 배포 완료여부 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>배포 완료여부</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_DIRECT"
                                checked={parkingLotStatus === "DIRECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "DIRECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_DIRECT">완료</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="campaign_type"
                                id="parkingLotStatus_SELECT"
                                checked={parkingLotStatus === "SELECT"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        parkingLotStatus: "SELECT",
                                    }));
                                }}
                            />
                            <label htmlFor="parkingLotStatus_SELECT">미완료</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 관리자 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>관리자 메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <ReactQuill
                            theme="snow"
                            style={{ width: "100%", height: "200px" }}
                            value={value}
                            onChange={setValue}
                            placeholder="관리자 메모를 작성해주세요."
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const ReleaseNoteTable = ({ formData, handleChange, setFormData, corpOptions, onDeleteItem }) => {
    const {
        name,
        rentCorpId,
        totalArea,
        totalCount,
        remainCount,
        releaseCount,
        lessor,
        lessorPhone,
        officeUseYn,
        address,
        addressDetail,
        bCode,
        parkingLotStatus,
        memo,
        firstDate,
    } = formData;
    const [value, setValue] = useState("");

    // 사무실 이용 여부 select options
    const officeUseOptions = [
        { value: "Y", label: "사용" },
        { value: "N", label: "미사용" },
    ];

    return (
        <>
            <div className="table-container">
                <div className="table-content">
                    <div className="table-wrapper">
                        <div className="table">
                            <table>
                                <colgroup>
                                    <col width="10%" />
                                    <col width="80%" />
                                    <col width="10%" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">구분</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">내용</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">관리</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        {/* APP/WEB/전체 구분 */}
                                        <td>
                                            <div className="cell" data-align="center">
                                                <span className="cell-desc color-main">APP</span>
                                            </div>
                                        </td>

                                        {/* 내용 */}
                                        <td>
                                            <div className="cell">
                                                <span className="cell-desc">내용</span>
                                            </div>
                                        </td>

                                        {/* 수정/삭제 버튼 */}
                                        <td>
                                            <div className="cell" data-align="center">
                                                <button
                                                    type="button"
                                                    className="btn-cell btn-outline"
                                                    onClick={() => {
                                                        // TODO: 수정 기능 구현
                                                        console.log("수정 버튼 클릭");
                                                    }}
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-cell btn-error"
                                                    onClick={() => onDeleteItem(1)} // 임시 ID: 1
                                                >
                                                    삭제
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
